import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { listingsApi } from "./listingsApi";
import { toApiChannel } from "./marketplaceApi";
import type { LinkableItem, SyncActivityEntry, ChannelCategory, CreateListingResult } from "./listingsApi";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

export const listingsKeys = {
  itemListings: (tenant: string | null, itemCode: string) =>
    ["marketplace-listings", "item", tenant, itemCode] as const,
  channelListings: (tenant: string | null, channel: string, page: number, limit: number, connectionName?: string) =>
    ["marketplace-listings", "channel", tenant, channel, page, limit, connectionName] as const,
  linkSearch: (tenant: string | null, query: string, excludeItemCode?: string) =>
    ["marketplace-listings", "link-search", tenant, query, excludeItemCode] as const,
  syncActivity: (tenant: string | null, itemCode: string) =>
    ["marketplace-listings", "sync-activity", tenant, itemCode] as const,
  channelCategories: (tenant: string | null, channel: string, connectionName: string, query: string) =>
    ["marketplace-listings", "categories", tenant, channel, connectionName, query] as const,
};

export function useItemListings(itemCode: string | undefined) {
  const tenant = useTenant();
  return useQuery({
    queryKey: listingsKeys.itemListings(tenant, itemCode || ""),
    queryFn: () => listingsApi.getItemListings(itemCode!),
    enabled: !!tenant && !!itemCode,
    staleTime: 30_000,
  });
}

export function useChannelListings(channel: string, page = 1, limit = 5, connectionName?: string) {
  const tenant = useTenant();
  const apiChannel = toApiChannel(channel);
  return useQuery({
    queryKey: listingsKeys.channelListings(tenant, apiChannel, page, limit, connectionName),
    queryFn: () => listingsApi.getChannelListings(apiChannel, undefined, limit, page, connectionName),
    enabled: !!tenant && !!channel,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Search items for manual linking (debounced query, server-side) */
export function useSearchLinkableItems(query: string, excludeItemCode?: string) {
  const tenant = useTenant();
  return useQuery<LinkableItem[]>({
    queryKey: listingsKeys.linkSearch(tenant, query, excludeItemCode),
    queryFn: () => listingsApi.searchLinkableItems(query, excludeItemCode),
    enabled: !!tenant && query.length >= 2,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

/** Move all listings from source item to target item */
export function useLinkItem() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ sourceItemCode, targetItemCode }: { sourceItemCode: string; targetItemCode: string }) =>
      listingsApi.linkItem(sourceItemCode, targetItemCode),
    onSuccess: (_data, variables) => {
      toast.success("Items linked successfully");
      queryClient.invalidateQueries({
        queryKey: listingsKeys.itemListings(tenant, variables.sourceItemCode),
      });
      queryClient.invalidateQueries({
        queryKey: listingsKeys.itemListings(tenant, variables.targetItemCode),
      });
      // Invalidate item detail queries
      queryClient.invalidateQueries({ queryKey: ["doc", "Item"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to link items");
    },
  });
}

/** Separate a listing into a new standalone Item */
export function useUnlinkListing() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({
      listingName,
      listingDoctype,
      parentItemCode: _parentItemCode,
    }: {
      listingName: string;
      listingDoctype: string;
      parentItemCode: string;
    }) => listingsApi.unlinkListing(listingName, listingDoctype),
    onSuccess: (_data, variables) => {
      toast.success("Listing unlinked successfully");
      queryClient.invalidateQueries({
        queryKey: listingsKeys.itemListings(tenant, variables.parentItemCode),
      });
      // Invalidate item detail queries
      queryClient.invalidateQueries({ queryKey: ["doc", "Item"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to unlink listing");
    },
  });
}

/** Get recent sync activity for an item (Plan 192, Epic 04) */
export function useItemSyncActivity(itemCode: string | undefined) {
  const tenant = useTenant();
  return useQuery<SyncActivityEntry[]>({
    queryKey: listingsKeys.syncActivity(tenant, itemCode || ""),
    queryFn: () => listingsApi.getItemSyncActivity(itemCode!),
    enabled: !!tenant && !!itemCode,
    staleTime: 30_000,
  });
}

/** Push current stock + price to a single channel listing (Plan 192, Epic 04) */
export function usePushItemToChannel() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation({
    mutationFn: ({ itemCode, channel, listingName }: {
      itemCode: string; channel: string; listingName: string;
    }) => listingsApi.pushItemToChannel(itemCode, channel, listingName),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: listingsKeys.syncActivity(tenant, variables.itemCode),
      });
      queryClient.invalidateQueries({
        queryKey: listingsKeys.itemListings(tenant, variables.itemCode),
      });
    },
    // No toast — component handles feedback via local state (checkmark/error text)
  });
}

// --- Product Creation (Plan 192, Epic 06) ---

/** Search channel categories for product listing — server-side filtered, debounced by caller */
export function useChannelCategories(channel: string, connectionName: string, query: string) {
  const tenant = useTenant();
  return useQuery<ChannelCategory[]>({
    queryKey: listingsKeys.channelCategories(tenant, channel, connectionName, query),
    queryFn: () => listingsApi.getChannelCategories(channel, connectionName, query),
    enabled: !!tenant && !!channel && !!connectionName,
    staleTime: 5 * 60_000, // 5 minutes — categories rarely change
    placeholderData: keepPreviousData,
  });
}

/** Create a new listing on a channel from an ERPNext item */
export function useCreateChannelListing() {
  const queryClient = useQueryClient();
  const tenant = useTenant();
  return useMutation<CreateListingResult, Error, {
    itemCode: string; channel: string; connectionName: string; categoryId: string;
  }>({
    mutationFn: ({ itemCode, channel, connectionName, categoryId }) =>
      listingsApi.createChannelListing(itemCode, channel, connectionName, categoryId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: listingsKeys.itemListings(tenant, variables.itemCode),
      });
      queryClient.invalidateQueries({
        queryKey: listingsKeys.syncActivity(tenant, variables.itemCode),
      });
      queryClient.invalidateQueries({ queryKey: ["doc", "Item"] });
    },
    // No toast — component handles feedback via sheet state
  });
}
